/** Grade level for Hong Kong curriculum */
export type GradeLevel =
  | 'P1' | 'P2' | 'P3' | 'P4' | 'P5' | 'P6'  // Primary
  | 'S1' | 'S2' | 'S3' | 'S4' | 'S5' | 'S6'; // Secondary

/** Question types that can appear in assignments */
export type QuestionType =
  | 'multiple_choice'      // 選擇題
  | 'fill_in_blank'        // 填充題
  | 'true_false'           // 是非題
  | 'short_answer'         // 短答題
  | 'reading_comprehension'// 閱讀理解
  | 'essay'                // 作文
  | 'dictation'            // 默書
  | 'mixed';               // 混合題型

/** Rubric criteria for grading */
export interface RubricCriterion {
  id: string;
  name: string;              // e.g., "內容", "組織", "文筆", "字詞"
  description: string;
  maxScore: number;
  scoringGuide?: string;     // Detailed scoring guidance
}

/** Complete rubric definition */
export interface Rubric {
  id: string;
  name: string;
  gradeLevel: GradeLevel;
  questionType: QuestionType;
  totalScore: number;
  criteria: RubricCriterion[];
  modelAnswer?: string;       // For objective questions
  keywords?: string[];        // Key points to look for
  createdAt?: string;
  updatedAt?: string;
}

/** Individual question result */
export interface QuestionResult {
  questionNumber: string | number;
  extractedAnswer: string;    // What the student wrote
  expectedAnswer?: string;    // Model answer if available
  score: number;
  maxScore: number;
  feedback: string;           // Feedback in Chinese
  confidence: number;         // 0-1, how confident the AI is
}

/** Essay-specific grading breakdown */
export interface EssayBreakdown {
  content: { score: number; maxScore: number; feedback: string };
  organization: { score: number; maxScore: number; feedback: string };
  language: { score: number; maxScore: number; feedback: string };
  mechanics: { score: number; maxScore: number; feedback: string };
}

// ============================================
// ESSAY-SPECIFIC TYPES
// ============================================

/** Essay overall level */
export type EssayLevel = '優秀' | '良好' | '尚可' | '待改進';

/** Essay dimension names */
export type EssayDimensionName = 'content' | 'organization' | 'language' | 'mechanics';

/** Band descriptors for each level */
export interface BandDescriptors {
  excellent: string;   // 優秀
  good: string;        // 良好
  fair: string;        // 尚可
  needsImprovement: string; // 待改進
}

/** Essay dimension configuration */
export interface EssayDimension {
  name: string;              // Display name e.g., "內容"
  weight: number;            // Percentage weight, e.g., 40
  maxScore: number;          // Maximum score for this dimension
  bandDescriptors: BandDescriptors;
}

/** Essay band/level definition */
export interface EssayBand {
  level: EssayLevel;
  scoreRange: [number, number];  // e.g., [85, 100]
  description: string;
}

/** Essay requirements */
export interface EssayRequirements {
  minWordCount?: number;
  maxWordCount?: number;
  requiredElements?: string[];  // e.g., ["比喻", "排比"]
}

/** Essay exemplar for reference */
export interface EssayExemplar {
  id?: string;
  level: EssayLevel;
  title?: string;
  content: string;
  commentary?: string;         // Why this essay is at this level
  scoreBreakdown?: {
    content: number;
    organization: number;
    language: number;
    mechanics: number;
    total: number;
  };
}

/** Essay-specific rubric configuration */
export interface EssayConfig {
  dimensions: {
    content: EssayDimension;
    organization: EssayDimension;
    language: EssayDimension;
    mechanics: EssayDimension;
  };
  bands: EssayBand[];
  requirements?: EssayRequirements;
  prompt?: string;              // Essay topic/prompt
  exemplars?: EssayExemplar[];
  examPreset?: string;          // Exam board preset used (e.g., 'dse_chinese', 'aqa_gcse')
}

/** Essay dimension score result */
export interface EssayDimensionScore {
  score: number;
  maxScore: number;
  level: EssayLevel;
  strengths: string[];
  improvements: string[];
  comments: string;
}

/** Highlighted passage from essay */
export interface EssayHighlight {
  quote: string;
  comment: string;
}

/** Correction found in essay */
export interface EssayCorrection {
  original: string;
  corrected: string;
  type: '錯別字' | '標點' | '語法';
}

/** Inline annotation types for essay display */
export type InlineAnnotationType = 
  | 'positive'     // 精彩片段 - green
  | 'structure'    // 結構問題 - purple  
  | 'language'     // 語言問題 - blue
  | 'content'      // 內容問題 - orange
  | 'error'        // 錯別字/標點/語法 - red
  | 'suggestion';  // 建議 - yellow

/** Bounding box for image annotation overlay (normalized 0-1 coordinates) */
export interface AnnotationBoundingBox {
  x: number;      // Left edge (0-1)
  y: number;      // Top edge (0-1)
  width: number;  // Width (0-1)
  height: number; // Height (0-1)
  pageIndex?: number; // Which page/image (0-indexed)
}

/** Inline annotation for essay text */
export interface InlineAnnotation {
  id: string;
  type: InlineAnnotationType;
  quote: string;           // The text to highlight (must match exactly in essay)
  comment: string;         // The feedback/comment for this annotation
  suggestion?: string;     // Optional suggested replacement
  dimension?: string;      // Which dimension this relates to (content/organization/language/mechanics)
  boundingBox?: AnnotationBoundingBox; // Optional bounding box for image overlay
}

/** Complete essay grading result */
export interface EssayGradingResult {
  id: string;
  assignmentId: string;
  pipelineRunId?: string;
  
  // Overall assessment
  overallLevel: EssayLevel;
  totalScore: number;
  maxTotalScore: number;
  
  // Dimension scores
  dimensions: {
    content: EssayDimensionScore;
    organization: EssayDimensionScore;
    language: EssayDimensionScore;
    mechanics: EssayDimensionScore;
  };
  
  // Highlights and corrections
  highlights: EssayHighlight[];
  corrections: EssayCorrection[];
  
  // Inline annotations for displaying on essay text
  inlineAnnotations?: InlineAnnotation[];
  
  // Feedback
  overallFeedback: string;
  suggestedImprovements: string[];
  
  // Metadata
  confidence: number;
  provider: string;
  modelVersion: string;
  processingTimeMs?: number;
  
  // Transcribed text
  transcribedText?: string;
  
  // Version control
  version: number;
  isCurrent: boolean;
  
  // Teacher adjustments
  teacherAdjusted: boolean;
  adjustedScore?: number;
  adjustedFeedback?: string;
  
  createdAt: string;
  updatedAt?: string;
}

/** Default essay dimension configurations */
export const DEFAULT_ESSAY_DIMENSIONS: EssayConfig['dimensions'] = {
  content: {
    name: '內容',
    weight: 40,
    maxScore: 40,
    bandDescriptors: {
      excellent: '立意明確新穎，內容豐富充實，思想感情真摯，見解獨到深刻',
      good: '主題明確，內容充實，感情真實，有一定見解',
      fair: '主題基本明確，內容較為切題，感情尚算真實',
      needsImprovement: '主題不明確或偏離題目，內容空泛或離題'
    }
  },
  organization: {
    name: '結構',
    weight: 25,
    maxScore: 25,
    bandDescriptors: {
      excellent: '結構嚴謹完整，層次分明，過渡自然流暢，首尾呼應',
      good: '結構完整，段落分明，過渡恰當',
      fair: '結構基本完整，段落較分明',
      needsImprovement: '結構混亂，段落不清，缺乏連貫性'
    }
  },
  language: {
    name: '語言',
    weight: 25,
    maxScore: 25,
    bandDescriptors: {
      excellent: '用詞精準優美，句式靈活多變，善用修辭，文采斐然',
      good: '用詞恰當，句子通順，能運用修辭手法',
      fair: '用詞基本正確，句子較通順',
      needsImprovement: '用詞不當，病句較多，表達不清'
    }
  },
  mechanics: {
    name: '字詞標點',
    weight: 10,
    maxScore: 10,
    bandDescriptors: {
      excellent: '無錯別字，標點正確，書寫工整美觀',
      good: '錯別字極少（1-2個），標點基本正確',
      fair: '有少量錯別字（3-5個），標點偶有錯誤',
      needsImprovement: '錯別字較多（6個以上），標點使用混亂'
    }
  }
};

/** Default essay bands */
export const DEFAULT_ESSAY_BANDS: EssayBand[] = [
  { level: '優秀', scoreRange: [85, 100], description: '內容豐富有深度，結構完整嚴謹，語言優美流暢，字詞標點準確' },
  { level: '良好', scoreRange: [70, 84], description: '內容充實，結構清晰，語言通順，字詞標點基本正確' },
  { level: '尚可', scoreRange: [50, 69], description: '內容基本切題，結構尚算完整，語言基本通順，有少量錯誤' },
  { level: '待改進', scoreRange: [0, 49], description: '內容空泛離題，結構混亂，語言問題較多，錯誤較多' }
];

/** Per-question result inside a student submission grading_result */
export interface SubmissionQuestionResult {
  questionId: string;
  marksAwarded: number;
  maxMarks: number;
  transcribedAnswer: string;
  feedback: string;
  confidence: number;
  needs_review?: boolean;
  teacher_score?: number | null;
  teacher_feedback?: string | null;
  reviewed_at?: string | null;
  reviewed_by?: string | null;
  rationale?: { criterion: string; marksAwarded: number; maxMarks: number; evidence: string }[];
  crop_data?: string | null;
}

/** Assignment status */
export type AssignmentStatus = 'draft' | 'ready_for_submission' | 'pending' | 'processing' | 'completed' | 'failed' | 'review';

/** Assignment record */
export interface Assignment {
  id: string;
  teacherId: string;
  rubricId?: string;
  studentName?: string;
  studentClass?: string;
  imagePaths: string[];
  gradeLevel: GradeLevel;
  questionType?: QuestionType;
  highStakes: boolean;
  status: AssignmentStatus;
  createdAt: string;
  updatedAt: string;
}

/** Complete grading result */
export interface GradingResult {
  id: string;
  assignmentId: string;
  provider: string;
  modelVersion: string;
  questions: QuestionResult[];
  essayBreakdown?: EssayBreakdown;
  totalScore: number;
  maxTotalScore: number;
  overallFeedback: string;
  confidence: number;
  needsReview: boolean;
  reviewReasons?: string[];
  extractedText?: string;
  processingTimeMs: number;
  tokenUsage?: {
    input: number;
    output: number;
  };
  teacherAdjusted: boolean;
  adjustedScore?: number;
  adjustedFeedback?: string;
  createdAt: string;
}

